import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule
} from '@angular/forms';
import { ModalComponent } from "../../../../core/components/modal/modal.component";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { HelperBannerComponent } from '../../../../core/components/helper-banner/helper-banner.component';
import { DecimalPipe, NgClass } from '@angular/common';
import {ToastrService} from 'ngx-toastr';
import {CalendarService} from '../../../services/calendar/calendar.service';

@Component({
  selector: 'app-calendar-add-timeslot-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslocoPipe,
    HelperBannerComponent,
    NgClass,
    ReactiveFormsModule,
    DecimalPipe
],
  templateUrl: './calendar-add-timeslot-modal.component.html',
  styleUrl: './calendar-add-timeslot-modal.component.scss'
})
export class CalendarAddTimeslotModalComponent implements OnInit {
  @Input() open = false;
  @Input() day: string | null = null;
  @Output() onClose = new EventEmitter<boolean>();

  form!: FormGroup;
  hours: number[] = Array.from({ length: 24 }, (_, i) => i);
  minutes: number[] = Array.from({ length: 12 }, (_, i) => i * 5);

  constructor(
    private readonly transloco: TranslocoService,
    private readonly fb: FormBuilder,
    private readonly planningService: CalendarService,
    private readonly toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      startHour: [null, Validators.required],
      startMinute: [null, Validators.required],
      endHour: [null, Validators.required],
      endMinute: [null, Validators.required]
    }, { validators: this.timeOrderValidator });
  }

  getDayTranslationKey(): string {
    if (!this.day) {
      return '';
    }
    return this.transloco.translate(`calendar.planning.weekdays.${this.day}`);
  }

  submit() {
    if (this.form.valid && this.day) {
      const { startHour, startMinute, endHour, endMinute } = this.form.value;
      const start = this.formatTime(startHour, startMinute);
      const end = this.formatTime(endHour, endMinute);
      this.planningService.addTimeslotToDay(this.day, { start, end }).subscribe({
        next: () => {
          this.close(true);
          this.toastr.success(
              this.transloco.translate('calendar.planning.add-timeslot.success.content', {'day': this.getDayTranslationKey()}),
              this.transloco.translate('calendar.planning.add-timeslot.success.title')
          );
        },
        error: (error) => {
          if (error.error.error === 'calendar.planning.add-timeslot.overlap') {
            this.toastr.error(
                this.transloco.translate('calendar.planning.add-timeslot.overlap.content', {'day': this.getDayTranslationKey()}),
                this.transloco.translate('calendar.planning.add-timeslot.overlap.title')
            );
          } else {
            this.toastr.error(
                this.transloco.translate('calendar.planning.add-timeslot.error.content', {'day': this.getDayTranslationKey()}),
                this.transloco.translate('calendar.planning.add-timeslot.error.title')
            );
          }
        }
      });
    }
  }

  formatTime(hour: number, minute: number): string {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }

  // Custom validator to ensure start < end
  timeOrderValidator(group: AbstractControl): ValidationErrors | null {
    const sh = group.get('startHour')?.value;
    const sm = group.get('startMinute')?.value;
    const eh = group.get('endHour')?.value;
    const em = group.get('endMinute')?.value;

    // Accept 0 as valid, but not null or empty string
    if (
      sh === null || sh === '' ||
      sm === null || sm === '' ||
      eh === null || eh === '' ||
      em === null || em === ''
    ) {
      return null; // Required validators will handle this
    }

    const start = Number(sh) * 60 + Number(sm);
    const end = Number(eh) * 60 + Number(em);
    if (start >= end) {
      return { timeOrder: true };
    }
    return null;
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  isTimeOrderInvalid(): boolean {
    return !!(this.form.errors && this.form.errors['timeOrder'] && this.form.touched);
  }

  close(updated: boolean) {
    this.onClose.emit(updated);
    this.form.reset();
  }
}
